import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { LogoMark } from '../components/Logo';
import { isWissenEmail } from '../utils/validate';

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ── Animated counting number ──────────────────────────────────────────────────
function CountUp({ target, suffix = '', duration = 1800 }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.5);
  useEffect(() => {
    if (!inView) return;
    const start = Date.now();
    const num = parseFloat(target.replace(/,/g, ''));
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(eased * num));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target, duration]);
  return (
    <span ref={ref}>
      {val >= 1000 ? val.toLocaleString() : val}{suffix}
    </span>
  );
}

// ── Floating particle layer ────────────────────────────────────────────────────
const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 2 + 0.5,
  dur: Math.random() * 14 + 8,
  delay: Math.random() * -14,
  opacity: Math.random() * 0.5 + 0.1,
}));

function Particles() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {PARTICLES.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`, top: `${p.y}%`,
          width: p.size, height: p.size,
          borderRadius: '50%',
          background: `rgba(0,212,255,${p.opacity})`,
          animation: `floatP ${p.dur}s ${p.delay}s ease-in-out infinite alternate`,
          boxShadow: `0 0 ${p.size * 3}px rgba(0,212,255,0.4)`,
        }} />
      ))}
    </div>
  );
}

// ── Section reveal wrapper ─────────────────────────────────────────────────────
function Reveal({ children, dir = 'up', delay = 0, threshold = 0.2, style = {} }) {
  const [ref, inView] = useInView(threshold);
  const transforms = { up: 'translateY(48px)', down: 'translateY(-48px)', left: 'translateX(-48px)', right: 'translateX(48px)', scale: 'scale(0.88)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : transforms[dir],
      transition: `opacity 0.8s ${delay}s cubic-bezier(.22,.68,0,1.2), transform 0.8s ${delay}s cubic-bezier(.22,.68,0,1.2)`,
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Rotating hex orb ─────────────────────────────────────────────────────────
function HexOrb({ size = 120, glow = true }) {
  return (
    <div style={{ width: size, height: size, position: 'relative', margin: '0 auto' }}>
      {/* outer spinning ring */}
      <div style={{
        position: 'absolute', inset: -size * 0.18,
        borderRadius: '50%',
        border: `1px solid transparent`,
        borderTopColor: 'var(--j1)', borderRightColor: 'var(--j2)',
        animation: 'spin 5s linear infinite',
        filter: glow ? 'drop-shadow(0 0 6px var(--glow1))' : 'none',
      }} />
      {/* counter-ring */}
      <div style={{
        position: 'absolute', inset: -size * 0.08,
        borderRadius: '50%',
        border: `1px solid transparent`,
        borderBottomColor: 'var(--j3)', borderLeftColor: 'var(--j2)',
        animation: 'spin 3.5s linear infinite reverse',
        opacity: 0.7,
      }} />
      {/* core */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,212,255,0.12) 0%, rgba(0,212,255,0.03) 60%, transparent 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid rgba(0,212,255,0.15)',
        animation: 'pulse 3s ease-in-out infinite',
      }}>
        <LogoMark size={Math.round(size * 0.56)} />
      </div>
    </div>
  );
}

// ── Data for sections ─────────────────────────────────────────────────────────
const STATS = [
  { value: '4', suffix: '', label: 'Membership Tiers' },
  { value: '17', suffix: '', label: 'Contribution Categories' },
  { value: '7000', suffix: '+', label: 'Max Bounty Points' },
  { value: '90', suffix: '', label: 'Day Pilot Window' },
];

const FEATURES = [
  {
    icon: '⚡', label: 'QUEST BOARD',
    title: 'Ship AI. Get Paid in Points.',
    body: 'Pre-approved quests across Individual, Pod, and Hybrid streams. Pick a bounty, build it, and watch the leaderboard move. No endless approvals.',
    accent: 'var(--j1)',
  },
  {
    icon: '🏆', label: 'BOUNTY SYSTEM',
    title: '17 Ways to Earn Recognition.',
    body: 'From LLM wrappers to full agentic pipelines — every contribution category is scoped, sized, and scored. Velocity multipliers reward shipping fast.',
    accent: 'var(--j2)',
  },
  {
    icon: '📚', label: 'USE CASE LIBRARY',
    title: 'Reuse Earns Royalties.',
    body: 'Every piece of adopted code becomes a reusable asset. Each time another team reuses your work, you earn loyalty points automatically.',
    accent: 'var(--j3)',
  },
  {
    icon: '🔬', label: 'POD EXECUTION',
    title: 'Small Teams. Real Deliverables.',
    body: 'Cross-functional Pods of 3–5 tackle production-grade AI solutions under SME oversight. Pod quests unlock the highest bounty multipliers.',
    accent: 'var(--j4)',
  },
  {
    icon: '⬡', label: 'JARVIS ASSISTANT',
    title: 'Your AI-Powered Navigator.',
    body: 'A voice-enabled AI that knows every rule, quest, and leaderboard. Ask it anything — it answers, navigates, and coaches you through the programme.',
    accent: 'var(--j1)',
  },
];

const TIERS = [
  { name: 'LEGEND', color: '#FFD700', glow: 'rgba(255,215,0,0.3)', desc: 'Top 5% contributors. Unlimited multiplier access.' },
  { name: 'VETERAN', color: '#C0C0FF', glow: 'rgba(192,192,255,0.3)', desc: '2,000+ pts. Speed & impact multipliers unlocked.' },
  { name: 'CORE', color: 'var(--j1)', glow: 'rgba(0,212,255,0.3)', desc: '500–1,999 pts. Active contributor. Peer review access.' },
  { name: 'PROBATION', color: 'var(--txt2)', glow: 'rgba(136,153,166,0.2)', desc: 'First 30 days. Audition sprint underway.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'Member / Associate', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Please enter your name'); return; }
    if (!isWissenEmail(form.email)) { setError('Please use your @wissen.com email address'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/waitlist', form);
      setDone(data.message || "You're on the list!");
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const heroOpacity = Math.max(0, 1 - scrollY / 400);
  const heroScale = Math.max(0.85, 1 - scrollY / 3000);

  return (
    <div style={{ background: 'var(--bg)', color: 'var(--txt)', overflowX: 'hidden' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { box-shadow: 0 0 20px rgba(0,212,255,0.15); } 50% { box-shadow: 0 0 40px rgba(0,212,255,0.35); } }
        @keyframes floatP { 0% { transform: translateY(0) translateX(0); } 100% { transform: translateY(-30px) translateX(15px); } }
        @keyframes scrollBounce { 0%,100% { transform: translateY(0); opacity:0.8; } 50% { transform: translateY(8px); opacity:0.3; } }
        @keyframes slideLeft { from { transform: translateX(100%); opacity:0; } to { transform: translateX(0); opacity:1; } }
        @keyframes gradShift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes orbFloat { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-16px) scale(1.03); } }
        .feat-card:hover { border-color: var(--brd) !important; transform: translateY(-4px) !important; }
        .tier-card:hover { transform: scale(1.04) !important; }
        .landing-nav { backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); }
      `}</style>

      {/* ── Sticky nav ────────────────────────────────────────────────────── */}
      <nav className="landing-nav" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 40px',
        background: scrollY > 40 ? 'rgba(2,4,8,0.92)' : 'transparent',
        borderBottom: scrollY > 40 ? '1px solid var(--brd2)' : '1px solid transparent',
        transition: 'background 0.4s, border-color 0.4s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LogoMark size={28} />
          <span style={{ fontFamily: 'Orbitron', fontSize: 16, fontWeight: 900, color: 'var(--j1)', letterSpacing: 3 }}>
            NEURAL<span style={{ color: 'var(--txt2)' }}>OPS</span>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {user ? (
            <button className="btn btn-primary" style={{ fontSize: 11, padding: '8px 20px', letterSpacing: 2 }} onClick={() => navigate('/app')}>
              ENTER PLATFORM →
            </button>
          ) : (
            <>
              <button className="btn" style={{ fontSize: 11, padding: '8px 18px', borderColor: 'var(--brd)', color: 'var(--txt2)' }} onClick={() => navigate('/login')}>
                LOG IN
              </button>
              <button className="btn btn-primary" style={{ fontSize: 11, padding: '8px 20px', letterSpacing: 2 }} onClick={() => setShowWaitlist(true)}>
                JOIN WAITLIST
              </button>
            </>
          )}
        </div>
      </nav>

      {/* ── SECTION 1: Hero ────────────────────────────────────────────────── */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden', paddingTop: 80,
      }}>
        <Particles />

        {/* grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(0,212,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.04) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%)',
        }} />

        {/* scan line */}
        <div style={{
          position: 'absolute', left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.3), transparent)',
          top: `${(scrollY * 0.3) % 100}vh`, pointerEvents: 'none',
        }} />

        <div style={{
          opacity: heroOpacity,
          transform: `scale(${heroScale})`,
          transition: 'none',
          textAlign: 'center', padding: '0 24px', position: 'relative', zIndex: 2,
        }}>
          <div style={{ animation: 'orbFloat 5s ease-in-out infinite', marginBottom: 40 }}>
            <HexOrb size={140} />
          </div>

          <div style={{
            fontFamily: 'Orbitron', fontWeight: 900,
            fontSize: 'clamp(32px, 7vw, 72px)',
            letterSpacing: 2, lineHeight: 1.05, marginBottom: 20,
            background: 'linear-gradient(135deg, var(--txt) 0%, var(--j1) 50%, var(--j2) 100%)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'gradShift 6s ease infinite',
          }}>
            ENGINEERING FASTER<br />THROUGH APPLIED AI
          </div>

          <div style={{ fontSize: 'clamp(14px,1.8vw,18px)', color: 'var(--txt2)', maxWidth: 600, margin: '0 auto 36px', lineHeight: 1.7 }}>
            Wissen Technology's AI Center of Excellence — where engineers claim quests,
            ship production-grade AI, and earn recognition for real, adopted work.
          </div>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" style={{ padding: '14px 32px', fontSize: 13, letterSpacing: 3 }}
              onClick={() => user ? navigate('/app') : setShowWaitlist(true)}>
              {user ? 'ENTER PLATFORM' : 'JOIN THE WAITLIST'}
            </button>
            {!user && (
              <button className="btn" style={{ padding: '14px 32px', fontSize: 13, letterSpacing: 3, borderColor: 'var(--brd)', color: 'var(--j1)' }}
                onClick={() => navigate('/login')}>
                MEMBER LOGIN
              </button>
            )}
          </div>
        </div>

        {/* scroll indicator */}
        <div style={{
          position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)',
          opacity: heroOpacity, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        }}>
          <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 3 }}>SCROLL TO EXPLORE</div>
          <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, var(--j1), transparent)', animation: 'scrollBounce 2s ease-in-out infinite' }} />
        </div>
      </section>

      {/* ── SECTION 2: Manifesto ───────────────────────────────────────────── */}
      <section style={{
        minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)',
        padding: '80px 24px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: 860 }}>
          <Reveal dir="up" threshold={0.3}>
            <div style={{ fontSize: 9, color: 'var(--j1)', fontFamily: 'Share Tech Mono', letterSpacing: 6, marginBottom: 28 }}>
              THE MANDATE
            </div>
            <div style={{
              fontFamily: 'Orbitron', fontWeight: 800,
              fontSize: 'clamp(26px, 5vw, 54px)',
              lineHeight: 1.15, color: 'var(--txt)',
              marginBottom: 28,
            }}>
              AI is not the future.<br />
              <span style={{ color: 'var(--j1)', textShadow: '0 0 40px var(--glow1)' }}>It's your job right now.</span>
            </div>
            <div style={{ fontSize: 16, color: 'var(--txt2)', lineHeight: 1.8, maxWidth: 680, margin: '0 auto' }}>
              NeuralOps is a structured innovation league — not a hackathon, not a side project.
              Every quest ships to production. Every contribution is scored. Every engineer
              has a visible ranking and a path to earn recognition and rewards.
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 3: Stats ───────────────────────────────────────────────── */}
      <section style={{
        padding: '80px 24px',
        borderTop: '1px solid var(--brd2)', borderBottom: '1px solid var(--brd2)',
        background: 'rgba(0,212,255,0.015)',
      }}>
        <Reveal dir="up" threshold={0.2}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ fontFamily: 'Orbitron', fontSize: 11, color: 'var(--txt3)', letterSpacing: 5 }}>BY THE NUMBERS</div>
          </div>
        </Reveal>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '40px 80px', maxWidth: 1000, margin: '0 auto' }}>
          {STATS.map((s, i) => (
            <Reveal key={s.label} dir="up" delay={i * 0.1} threshold={0.4}>
              <div style={{ textAlign: 'center', minWidth: 160 }}>
                <div style={{
                  fontFamily: 'Orbitron', fontWeight: 900,
                  fontSize: 'clamp(40px, 6vw, 64px)', color: 'var(--j1)',
                  textShadow: '0 0 30px var(--glow1)',
                  lineHeight: 1,
                }}>
                  <CountUp target={s.value} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 10, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 2, marginTop: 10, textTransform: 'uppercase' }}>
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── SECTION 4: Feature slides ──────────────────────────────────────── */}
      <section style={{ padding: '100px 24px 60px' }}>
        <Reveal dir="up" threshold={0.2}>
          <div style={{ textAlign: 'center', marginBottom: 72 }}>
            <div style={{ fontFamily: 'Orbitron', fontSize: 11, color: 'var(--txt3)', letterSpacing: 5, marginBottom: 16 }}>
              THE PLATFORM
            </div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 'clamp(22px,3.5vw,38px)', fontWeight: 800, color: 'var(--txt)' }}>
              Built for engineers who ship.
            </div>
          </div>
        </Reveal>

        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 64 }}>
          {FEATURES.map((f, i) => (
            <div key={f.label} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 40, alignItems: 'center',
            }}>
              <Reveal dir={i % 2 === 0 ? 'left' : 'right'} delay={0} threshold={0.2}>
                <div className="card feat-card" style={{
                  padding: '36px 32px',
                  borderColor: 'var(--brd2)',
                  transition: 'border-color 0.3s, transform 0.3s',
                  cursor: 'default',
                  order: i % 2 === 0 ? 0 : 1,
                }}>
                  <div style={{ fontSize: 10, color: f.accent, fontFamily: 'Share Tech Mono', letterSpacing: 4, marginBottom: 12 }}>
                    {f.label}
                  </div>
                  <div style={{ fontFamily: 'Orbitron', fontSize: 'clamp(18px,2.5vw,26px)', fontWeight: 800, color: 'var(--txt)', marginBottom: 16, lineHeight: 1.2 }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--txt2)', lineHeight: 1.8 }}>
                    {f.body}
                  </div>
                </div>
              </Reveal>

              <Reveal dir={i % 2 === 0 ? 'right' : 'left'} delay={0.15} threshold={0.2}>
                <div style={{
                  order: i % 2 === 0 ? 1 : 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{
                    width: 180, height: 180, borderRadius: '50%',
                    border: `1px solid ${f.accent}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                    background: `radial-gradient(circle, ${f.accent}08 0%, transparent 70%)`,
                    animation: 'orbFloat 4s ease-in-out infinite',
                    animationDelay: `${i * 0.5}s`,
                  }}>
                    <div style={{
                      position: 'absolute', inset: -10,
                      borderRadius: '50%',
                      border: `1px solid transparent`,
                      borderTopColor: f.accent,
                      borderRightColor: f.accent + '60',
                      animation: 'spin 6s linear infinite',
                      opacity: 0.6,
                    }} />
                    <div style={{ fontSize: 72 }}>{f.icon}</div>
                  </div>
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* ── SECTION 5: Tiers ───────────────────────────────────────────────── */}
      <section style={{
        padding: '100px 24px',
        background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,212,255,0.03) 0%, transparent 70%)',
        borderTop: '1px solid var(--brd2)',
      }}>
        <Reveal dir="up" threshold={0.2}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontFamily: 'Orbitron', fontSize: 11, color: 'var(--txt3)', letterSpacing: 5, marginBottom: 16 }}>MEMBERSHIP TIERS</div>
            <div style={{ fontFamily: 'Orbitron', fontSize: 'clamp(20px,3vw,36px)', fontWeight: 800 }}>
              Rank is earned.<br />
              <span style={{ color: 'var(--j1)' }}>Every week.</span>
            </div>
          </div>
        </Reveal>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', maxWidth: 1000, margin: '0 auto' }}>
          {TIERS.map((t, i) => (
            <Reveal key={t.name} dir="up" delay={i * 0.1} threshold={0.2}>
              <div className="tier-card" style={{
                width: 210, padding: '28px 20px', textAlign: 'center',
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${t.color}30`,
                borderRadius: 8,
                boxShadow: `0 0 30px ${t.glow}`,
                transition: 'transform 0.3s',
                cursor: 'default',
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  border: `2px solid ${t.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                  boxShadow: `0 0 20px ${t.glow}`,
                  background: `radial-gradient(circle, ${t.glow} 0%, transparent 70%)`,
                }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: t.color, opacity: 0.9 }} />
                </div>
                <div style={{ fontFamily: 'Orbitron', fontSize: 14, fontWeight: 800, color: t.color, letterSpacing: 2, marginBottom: 10 }}>
                  {t.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--txt2)', lineHeight: 1.6 }}>{t.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── SECTION 6: CTA ─────────────────────────────────────────────────── */}
      <section style={{
        minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '80px 24px', position: 'relative', overflow: 'hidden',
        borderTop: '1px solid var(--brd2)',
      }}>
        <Particles />
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 2, maxWidth: 680 }}>
          <Reveal dir="scale" threshold={0.3}>
            <HexOrb size={100} />
            <div style={{ marginTop: 40, fontFamily: 'Orbitron', fontSize: 'clamp(24px,4vw,44px)', fontWeight: 900, lineHeight: 1.2, marginBottom: 20 }}>
              Ready to compete<br />to contribute?
            </div>
            <div style={{ fontSize: 15, color: 'var(--txt2)', lineHeight: 1.7, marginBottom: 36 }}>
              Membership is earned, not given. Join the waitlist — the CWG reviews
              every request and will be in touch if you're selected for an audition sprint.
            </div>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" style={{ padding: '16px 40px', fontSize: 14, letterSpacing: 3 }}
                onClick={() => user ? navigate('/app') : setShowWaitlist(true)}>
                {user ? 'ENTER PLATFORM' : 'REQUEST ACCESS'}
              </button>
              {!user && (
                <button className="btn" style={{ padding: '16px 32px', fontSize: 14, letterSpacing: 3, borderColor: 'var(--brd)', color: 'var(--j1)' }}
                  onClick={() => navigate('/login')}>
                  MEMBER LOGIN
                </button>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div style={{
        padding: '24px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12,
        borderTop: '1px solid var(--brd2)', background: 'rgba(0,0,0,0.4)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={20} />
          <span style={{ fontFamily: 'Share Tech Mono', fontSize: 10, color: 'var(--txt3)', letterSpacing: 2 }}>
            NEURALOPS COE · WISSEN TECHNOLOGY
          </span>
        </div>
        <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Share Tech Mono', letterSpacing: 1 }}>
          INTERNAL CONFIDENTIAL · BENGALURU
        </div>
      </div>

      {/* ── Waitlist modal ─────────────────────────────────────────────────── */}
      {showWaitlist && (
        <div className="modal-overlay" onClick={() => !submitting && setShowWaitlist(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowWaitlist(false)}>×</button>
            {done ? (
              <div style={{ textAlign: 'center', padding: '20px 8px' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <div className="modal-title" style={{ marginBottom: 12 }}>YOU'RE ON THE LIST</div>
                <div style={{ fontSize: 13, color: 'var(--txt2)', lineHeight: 1.7, marginBottom: 24 }}>{done}</div>
                <button className="btn btn-primary" onClick={() => {
                  setShowWaitlist(false); setDone(null);
                  setForm({ name: '', email: '', role: 'Member / Associate', note: '' });
                }}>DONE</button>
              </div>
            ) : (
              <form onSubmit={joinWaitlist}>
                <div className="modal-title">JOIN THE WAITLIST</div>
                <div style={{ fontSize: 12, color: 'var(--txt2)', marginBottom: 20, lineHeight: 1.6 }}>
                  Selection is by nomination + audition sprint. The CWG reviews every request.
                </div>
                <div className="form-group">
                  <label className="label">Full Name</label>
                  <input className="input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your name" required />
                </div>
                <div className="form-group">
                  <label className="label">Work Email</label>
                  <input className="input" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="you@wissen.com" required />
                </div>
                <div className="form-group">
                  <label className="label">I'm interested as a…</label>
                  <select className="input" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
                    <option>Member / Associate</option>
                    <option>AI Engineer</option>
                    <option>SME / Project Auditor</option>
                    <option>Stakeholder / Sponsor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">Why do you want to join? <span style={{ color: 'var(--txt3)' }}>(optional)</span></label>
                  <textarea className="input" rows={3} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="A line about your AI interest or experience…" />
                </div>
                {error && <div style={{ color: 'var(--j6)', fontSize: 12, fontFamily: 'Share Tech Mono', marginBottom: 14 }}>{error}</div>}
                <button className="btn btn-primary" type="submit" disabled={submitting} style={{ width: '100%', padding: 13, letterSpacing: 2 }}>
                  {submitting ? 'SUBMITTING…' : 'REQUEST ACCESS'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: 'var(--txt3)' }}>
                  Already a member?{' '}
                  <span style={{ color: 'var(--j1)', cursor: 'pointer' }}
                    onClick={() => { setShowWaitlist(false); navigate('/login'); }}>
                    Log in
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
